// Host-scoped reads: `marketplaceHostOf(el)` returns the nearest
// island's state (or app root state in standalone mode), so `host.selectedItem`
// / `host.update(...)` / etc. stay isolated when multiple marketplace
// embeds share a page.
//
// `isThumbKind` + `getKindAccent` are registered marketplace functions
// (exported from functions/index.js + bundled via context.js) — reach
// them via `el.call(...)` per CLAUDE.md rule: no function imports
// between project files.

export const MarketplaceDetail = {
  flow: 'y',
  hide: (el) => {
    const host = el.call('marketplaceHostOf', el)
    return !host?.selectedItem
  },
  width: 'H',
  minWidth: 'H',
  borderLeft: '1px solid line',
  overflow: 'y auto',

  // Reset the pane's scroll position whenever `selectedItem` changes —
  // fresh item, fresh view. Without this, clicking Back / a breadcrumb
  // / a Similar row keeps the previous item's scrollTop, so users may
  // land mid-way through the new item's About section. Caches the
  // last id on the element so we don't scroll when other reactive
  // props (hover, etc.) trigger onUpdate.
  onUpdate: (el) => {
    const host = el.call('marketplaceHostOf', el)
    const id = host?.selectedItem?.id || null
    if (el._lastSelectedId === id) return
    el._lastSelectedId = id
    if (typeof el.scrollTo === 'function') el.scrollTo(0, 0)
  },

  DetailTopBar: {
    flow: 'x',
    align: 'center space-between',
    padding: 'Y A',
    borderBottom: '1px solid line',
    // Sticky so Close/Back stay visible as the user scrolls through
    // long descriptions + similar-items lists. Pane scrolls via the
    // root's `overflow: y auto` so `top: 0` anchors to the pane.
    position: 'sticky',
    top: '0',
    background: 'dialog',
    zIndex: '2',

    // Left-side row: Back arrow (hidden when history empty) + close.
    DetailTopLeft: {
      flow: 'x',
      gap: 'Y',
      align: 'center',

      // Appears after clicking through a "Similar item" — lets the user
      // retrace their steps. Pops the last selectedItem off history.
      // Hidden when `selectedHistory` is empty (i.e. fresh navigation
      // from the grid).
      DetailBackBtn: {
        extends: 'Button',
        text: '← Back',
        ariaLabel: 'Go back to previous item',
        hide: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return !(host?.selectedHistory?.length)
        },
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf', el)
          el.call('popSelectedHistory', host)
        }
      },

      // Breadcrumb trail of prior selections when the user has
      // drilled in via "Similar items" ≥2 levels. Each chip jumps
      // directly to that item + truncates the stack — faster than
      // repeated Back clicks for deep trails. Hidden when history
      // has 0 or 1 entry (Back alone covers 1 step).
      DetailBreadcrumb: {
        flow: 'x',
        gap: 'Y',
        align: 'center',
        hide: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return !(host?.selectedHistory?.length > 1)
        },
        children: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const history = host?.selectedHistory || []
          return history.map((item, idx) => ({
            ...item,
            _historyIndex: idx
          }))
        },
        childExtends: 'MarketplaceBreadcrumbItem'
      },

      // Pin / unpin the open detail item — mirrors the card/row star
      // so users don't need to close to pin. Star is yellow/filled
      // when pinned, outlined otherwise.
      DetailPinBtn: {
        extends: 'Button',
        text: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return el.call('isPinned', host, host?.selectedItem?.id) ? '★' : '☆'
        },
        color: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return el.call('isPinned', host, host?.selectedItem?.id) ? 'yellow' : 'caption'
        },
        title: 'Pin to sidebar',
        ariaLabel: 'Toggle pin',
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf', el)
          el.call('togglePin', host, host?.selectedItem?.id)
        }
      },

      DetailCloseBtn: {
        extends: 'Button',
        text: '✕',
        ariaLabel: 'Close detail pane',
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf', el)
          // `closeDetail` clears both selectedItem + selectedHistory
          // AND restores focus to the card the user originally opened,
          // preserving the Tab loop for keyboard users.
          el.call('closeDetail', host)
        }
      }
    }
  },

  DetailHero: {
    hide: (el) => {
      const host = el.call('marketplaceHostOf', el)
      return !el.call('isThumbKind', host?.selectedItem?.kind)
    },
    aspectRatio: '16 / 9',
    borderBottom: '1px solid line',

    DetailHeroImage: {
      hide: (el) => {
        const host = el.call('marketplaceHostOf', el)
        return !host?.selectedItem?.thumbnail
      },
      width: '100%',
      height: '100%',
      background: (el) => {
        const host = el.call('marketplaceHostOf', el)
        return `url("${host?.selectedItem?.thumbnail || ''}") center / cover no-repeat`
      },
      backgroundColor: 'dialog',
      __order: ['hide', 'width', 'height', 'background', 'backgroundColor']
    },
    DetailHeroPlaceholder: {
      hide: (el) => {
        const host = el.call('marketplaceHostOf', el)
        return !!host?.selectedItem?.thumbnail
      },
      width: '100%',
      height: '100%',
      background: 'transparentBg',
      backgroundColor: 'document',
      backgroundSize: '20px 20px',
      backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
      backgroundRepeat: 'repeat',
      __order: ['hide', 'width', 'height', 'background', 'backgroundColor', 'backgroundSize', 'backgroundPosition', 'backgroundRepeat']
    }
  },

  DetailBody: {
    flow: 'y',
    padding: 'A',
    gap: 'B',

    DetailHeader: {
      flow: 'x',
      gap: 'A',
      align: 'flex-start',

      DetailAvatar: {
        hide: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return el.call('isThumbKind', host?.selectedItem?.kind)
        },
        align: 'center center',
        width: 'C',
        height: 'C',
        minWidth: 'C',
        borderRadius: 'Z',
        fontWeight: '700',
        fontSize: 'B',
        overflow: 'hidden',
        background: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return host?.selectedItem?.logo ? 'white' : el.call('getKindAccent', host?.selectedItem?.kind, 'bg')
        },
        color: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return el.call('getKindAccent', host?.selectedItem?.kind, 'fg')
        },

        // Nested DOMQL img/letter pattern — Rule 31 (no html: strings
        // with inline style=). Host is read once per child via
        // `marketplaceHostOf`; each child hide-gates on selectedItem.logo.
        DetailAvatarImg: {
          hide: (el) => {
            const host = el.call('marketplaceHostOf', el)
            return !host?.selectedItem?.logo || el.parent._imgFailed
          },
          tag: 'img',
          src: (el) => {
            const host = el.call('marketplaceHostOf', el)
            const slug = host?.selectedItem?.logo
            return slug ? `https://cdn.simpleicons.org/${slug}` : ''
          },
          alt: (el) => {
            const host = el.call('marketplaceHostOf', el)
            return host?.selectedItem?.name || ''
          },
          width: '60%',
          height: '60%',
          objectFit: 'contain',
          onRender: (el) => {
            if (!el.node) return
            el.node.onerror = () => {
              el.parent._imgFailed = true
              el.node.style.display = 'none'
              const letter = el.parent.DetailAvatarLetter
              if (letter?.node) letter.node.style.display = ''
            }
          }
        },
        DetailAvatarLetter: {
          hide: (el) => {
            const host = el.call('marketplaceHostOf', el)
            return !!host?.selectedItem?.logo && !el.parent._imgFailed
          },
          tag: 'span',
          text: (el) => {
            const host = el.call('marketplaceHostOf', el)
            const name = host?.selectedItem?.name || ''
            return name[0]?.toUpperCase() || '?'
          }
        }
      },

      DetailMeta: {
        flow: 'y',
        gap: 'Y',
        flex: '1',

        DetailName: {
          tag: 'h3',
          text: (el) => {
            const host = el.call('marketplaceHostOf', el)
            return host?.selectedItem?.name
          },
          fontSize: 'B',
          fontWeight: '700',
          color: 'title'
        },

        DetailTagRow: {
          flow: 'x',
          gap: 'Y',

          DetailKindTag: {
            tag: 'span',
            text: (el) => {
              const host = el.call('marketplaceHostOf', el)
              return host?.selectedItem?.kind
            },
            // Native HTML title — mirrors MarketplaceCard.CardThumbBadge
            // tooltip pattern (tick 9).
            title: (el) => {
              const host = el.call('marketplaceHostOf', el)
              return `Kind: ${host?.selectedItem?.kind || ''}`
            },
            padding: 'X Y',
            borderRadius: 'Y',
            fontSize: 'Y',
            background: 'line',
            color: 'caption',
            textTransform: 'capitalize'
          },

          DetailCategoryTag: {
            tag: 'span',
            text: (el) => {
              const host = el.call('marketplaceHostOf', el)
              return host?.selectedItem?.category
            },
            title: (el) => {
              const host = el.call('marketplaceHostOf', el)
              return `Category: ${host?.selectedItem?.category || ''}`
            },
            padding: 'X Y',
            borderRadius: 'Y',
            fontSize: 'Y',
            background: 'line',
            color: 'caption',
            textTransform: 'capitalize'
          }
        }
      }
    },

    DetailAbout: {
      flow: 'y',
      gap: 'Y',

      DetailAboutTitle: {
        tag: 'h4',
        text: 'About',
        fontSize: 'Z',
        fontWeight: '600',
        color: 'title'
      },

      DetailAboutText: {
        tag: 'p',
        text: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return host?.selectedItem?.description
        },
        fontSize: 'Z',
        color: 'paragraph'
      }
    },

    DetailInstallInfo: {
      flow: 'y',
      gap: 'Y',

      DetailInstallTitle: {
        tag: 'h4',
        text: 'How it installs',
        fontSize: 'Z',
        fontWeight: '600',
        color: 'title'
      },

      DetailInstallNote: {
        tag: 'p',
        text: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const k = host?.selectedItem?.kind
          if (k === 'library') return 'Added to sharedLibraries.js and immediately available as shared components across your project.'
          if (k === 'template') return 'Copies pages, components, and design system files into your project source, then compiles with frank.'
          if (k === 'integration') return 'Adds the required npm dependencies, env variable stubs, and registers the integration functions so you can call them via el.call().'
          if (k === 'registrar') return 'Connects your registrar account so you can search, register, and manage domains — plus configure DNS — from inside the editor.'
          if (k === 'plugin') return 'Registers the plugin in the editor plugin registry and activates it on the next editor reload.'
          return ''
        },
        fontSize: 'Y',
        color: 'caption'
      }
    },

    DetailPriceCard: {
      flow: 'x',
      align: 'center space-between',
      padding: 'A',
      borderRadius: 'Z',
      border: '1px solid line',
      background: 'dialog',
      gap: 'A',

      DetailPriceInfo: {
        flow: 'y',
        gap: 'X',

        DetailPriceValue: {
          tag: 'h3',
          text: (el) => {
            const host = el.call('marketplaceHostOf', el)
            return host?.selectedItem?.price > 0 ? `$${host?.selectedItem?.price}` : 'Free'
          },
          fontSize: 'B',
          fontWeight: '700',
          color: (el) => {
            const host = el.call('marketplaceHostOf', el)
            return host?.selectedItem?.price > 0 ? 'title' : 'green'
          }
        },

        DetailPriceNote: {
          text: (el) => {
            const host = el.call('marketplaceHostOf', el)
            return host?.selectedItem?.price > 0 ? 'One-time purchase' : 'Open source'
          },
          fontSize: 'Y',
          color: 'caption'
        }
      },

      DetailInstallBtn: {
        extends: 'Button',
        theme: 'primary',
        text: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return el.call('installButtonText', host?.selectedItem, host)
        },
        disabled: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return el.call('installButtonDisabled', host?.selectedItem, host)
        },
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf', el)
          return el.call('dispatchInstallAndMark', host?.selectedItem, host)
        }
      }
    },

    // "Similar items" — up to 4 items with the same category as the
    // current selection, excluding self. Clicking one becomes the new
    // `selectedItem`. Hidden when no similar items exist.
    DetailSimilar: {
      flow: 'y',
      gap: 'Y',
      hide: (el) => {
        const host = el.call('marketplaceHostOf', el)
        const cur = host?.selectedItem
        if (!cur) return true
        const count = (host?.catalog || []).filter(
          (i) => i.id !== cur.id && i.category === cur.category
        ).length
        return count === 0
      },

      DetailSimilarTitle: {
        tag: 'h4',
        text: 'Similar items',
        fontSize: 'Z',
        fontWeight: '600',
        color: 'title'
      },

      DetailSimilarList: {
        flow: 'y',
        gap: 'Y',
        children: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const cur = host?.selectedItem
          if (!cur) return []
          return (host?.catalog || [])
            .filter((i) => i.id !== cur.id && i.category === cur.category)
            .slice(0, 4)
        },
        childExtends: 'MarketplaceDetailSimilarItem'
      }
    }
  }
}
